// Demo data fixtures for coach demo mode
// This data is shown to new coaches to demonstrate the product value

export interface DemoLeader {
  id: string;
  full_name: string;
  email: string;
  lifecycle_state: "active";
  behaviorName: string;
  behaviorTheme: string;
  phase: "ACTIVE_PRACTICE" | "CONSOLIDATION" | "SYNTHESIS" | "GRADUATED";
  streak: number;
  totalPracticeDays: number;
  lastPracticeDate: string;
  hasEnrichment: boolean;
}

export interface DemoSignal {
  id: string;
  leaderId: string;
  leaderName: string;
  signalType: "graduation_check" | "sequence_check" | "transcript_review" | "guidance_request";
  priority: number;
  behaviorName: string;
  reason: string;
  severity: "low" | "medium" | "high";
}

export interface DemoTranscriptAnalysis {
  id: string;
  leaderId: string;
  leaderName: string;
  meetingTitle: string;
  analyzedAt: string;
  strengths: string[];
  growthEdges: string[];
  keyInsight: string;
  nextExperiment: string;
}

// Demo Leaders (5)
export const demoLeaders: DemoLeader[] = [
  {
    id: "demo-leader-1",
    full_name: "Alex Chen",
    email: "alex.chen@demo.veera.coach",
    lifecycle_state: "active",
    behaviorName: "Active Listening",
    behaviorTheme: "CONNECT",
    phase: "CONSOLIDATION",
    streak: 12,
    totalPracticeDays: 28,
    lastPracticeDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    hasEnrichment: true,
  },
  {
    id: "demo-leader-2",
    full_name: "Jordan Smith",
    email: "jordan.smith@demo.veera.coach",
    lifecycle_state: "active",
    behaviorName: "Ask Don't Tell",
    behaviorTheme: "ELEVATE",
    phase: "ACTIVE_PRACTICE",
    streak: 5,
    totalPracticeDays: 12,
    lastPracticeDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    hasEnrichment: false,
  },
  {
    id: "demo-leader-3",
    full_name: "Taylor Johnson",
    email: "taylor.johnson@demo.veera.coach",
    lifecycle_state: "active",
    behaviorName: "Pause Before Responding",
    behaviorTheme: "RESPOND",
    phase: "SYNTHESIS",
    streak: 21,
    totalPracticeDays: 45,
    lastPracticeDate: new Date(Date.now() - 0 * 24 * 60 * 60 * 1000).toISOString(),
    hasEnrichment: true,
  },
  {
    id: "demo-leader-4",
    full_name: "Morgan Lee",
    email: "morgan.lee@demo.veera.coach",
    lifecycle_state: "active",
    behaviorName: "Acknowledge First",
    behaviorTheme: "CONNECT",
    phase: "ACTIVE_PRACTICE",
    streak: 2,
    totalPracticeDays: 4,
    lastPracticeDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    hasEnrichment: false,
  },
  {
    id: "demo-leader-5",
    full_name: "Casey Williams",
    email: "casey.williams@demo.veera.coach",
    lifecycle_state: "active",
    behaviorName: "Active Listening",
    behaviorTheme: "CONNECT",
    phase: "GRADUATED",
    streak: 45,
    totalPracticeDays: 60,
    lastPracticeDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    hasEnrichment: true,
  },
];

// Demo Signals/Attention Items (4)
export const demoSignals: DemoSignal[] = [
  {
    id: "demo-signal-1",
    leaderId: "demo-leader-3",
    leaderName: "Taylor Johnson",
    signalType: "graduation_check",
    priority: 1,
    behaviorName: "Pause Before Responding",
    reason: "Taylor is ready for graduation review — consistent high performance over 3 weeks",
    severity: "high",
  },
  {
    id: "demo-signal-2",
    leaderId: "demo-leader-2",
    leaderName: "Jordan Smith",
    signalType: "transcript_review",
    priority: 2,
    behaviorName: "Ask Don't Tell",
    reason: "New transcript from yesterday needs review",
    severity: "medium",
  },
  {
    id: "demo-signal-3",
    leaderId: "demo-leader-1",
    leaderName: "Alex Chen",
    signalType: "sequence_check",
    priority: 2,
    behaviorName: "Active Listening",
    reason: "Engagement has been steady for 2 weeks — consider sequencing",
    severity: "medium",
  },
  {
    id: "demo-signal-4",
    leaderId: "demo-leader-4",
    leaderName: "Morgan Lee",
    signalType: "guidance_request",
    priority: 3,
    behaviorName: "Acknowledge First",
    reason: "Just started — check in after day 7",
    severity: "low",
  },
];

// Demo Transcript Analysis
export const demoTranscriptAnalysis: DemoTranscriptAnalysis = {
  id: "demo-transcript-1",
  leaderId: "demo-leader-2",
  leaderName: "Jordan Smith",
  meetingTitle: "Weekly Team Standup",
  analyzedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  strengths: [
    "Effectively used open-ended questions to draw out team members' perspectives",
    "Demonstrated patience by allowing pauses for reflection before responding",
  ],
  growthEdges: [
    "Tendency to offer solutions when team members express challenges — opportunity to ask 'What have you tried?' before suggesting",
  ],
  keyInsight: "Jordan shows natural curiosity but defaults to problem-solving mode when stress increases.",
  nextExperiment: "In the next 1:1, practice counting to 3 before responding when a direct report shares a challenge.",
};

// Portfolio snapshot for demo mode
export const demoPortfolioSnapshot = {
  totalActiveLeaders: 4,
  themeDistribution: [
    { theme: "CONNECT", count: 3 },
    { theme: "ELEVATE", count: 1 },
    { theme: "RESPOND", count: 1 },
  ],
  enrichedJourneyPercent: 60,
};

// Phase distribution for demo mode
export const demoPhaseDistribution = [
  { phase: "ACTIVE_PRACTICE", count: 2 },
  { phase: "CONSOLIDATION", count: 1 },
  { phase: "SYNTHESIS", count: 1 },
  { phase: "GRADUATED", count: 1 },
];

// Momentum indicators (fake sparkline data)
export const demoMomentumIndicators = [
  {
    id: "practice",
    label: "Practice moments",
    trend: "increasing" as const,
    description: "Practice activity is increasing across your portfolio.",
    sparklineData: [3, 5, 6, 8],
  },
  {
    id: "reflections",
    label: "Reflection Sharing",
    trend: "stable" as const,
    description: "Reflection sharing remains consistent across your portfolio.",
    sparklineData: [4, 4, 5, 4],
  },
  {
    id: "transcripts",
    label: "Transcript Uploads",
    trend: "increasing" as const,
    description: "Transcript usage is increasing across your portfolio.",
    sparklineData: [1, 2, 3, 5],
  },
];

// Transcript trends for demo mode
export const demoTranscriptTrends = [
  {
    id: "frequency",
    label: "Upload Frequency",
    trend: "increasing" as const,
    description: "Transcript uploads are becoming more frequent across your portfolio.",
  },
  {
    id: "stability",
    label: "Behavior Marker Consistency",
    trend: "stable" as const,
    description: "Behavior marker consistency remains stable across transcripts.",
  },
  {
    id: "alignment",
    label: "Self-Report Alignment",
    trend: "increasing" as const,
    description: "Self-reports and transcript indicators show directional alignment.",
  },
];
